// src/hooks/useFeatureFlag.js
import { useState, useEffect } from 'react'
import { useFeatureFlagsContext } from '../auth/AuthProvider'

const useFeatureFlag = (featureName) => {
    const [isEnabled, setIsEnabled] = useState(false)
    const featureFlagsContext = useFeatureFlagsContext()

    useEffect(() => {
        // Check if the feature name is valid
        if (!featureName) {
            console.warn('useFeatureFlag: featureName was not provided.')
            setIsEnabled(false)
            return
        }

        // Check if the feature is enabled in environment variables
        const envFeatureFlags = process.env.REACT_APP_FEATURE_FLAGS || ''
        const envFeatureList = envFeatureFlags.split(',').map((f) => f.trim())
        const isEnabledInEnv = envFeatureList.includes(featureName)

        if (!isEnabledInEnv) {
            console.log(`useFeatureFlag: ${featureName} is not enabled in environment variables.`)
            setIsEnabled(false)
            return
        }

        // If flags haven't loaded yet, don't enable the feature
        if (!featureFlagsContext.loaded) {
            console.log(`useFeatureFlag: Feature flags not loaded yet for ${featureName}, waiting for API data.`)
            setIsEnabled(false)
            return
        }

        // Now we know flags are loaded and feature is in env vars
        console.log(`useFeatureFlag: Feature flags loaded, checking ${featureName}...`)

        const { flags } = featureFlagsContext

        // Validate flags structure
        if (!flags || !Array.isArray(flags.system) || typeof flags.tenant !== 'object') {
            console.warn(`useFeatureFlag: Invalid flags structure for ${featureName}, feature will be disabled.`)
            setIsEnabled(false)
            return
        }

        // Find system config for this feature
        const systemFeatureConfig = flags.system.find((item) => item.featureName === featureName)
        const tenantSettingForFeature = flags.tenant[featureName]

        // If system config is missing, feature is disabled
        if (!systemFeatureConfig || typeof systemFeatureConfig !== 'object') {
            console.warn(`useFeatureFlag: System configuration for '${featureName}' is missing, feature will be disabled.`)
            setIsEnabled(false)
            return
        }

        // Parse system settings
        const systemEnabled = parseInt(systemFeatureConfig.systemEnabled, 10)
        const isSystemOverride = parseInt(systemFeatureConfig.isSystemOverride, 10)

        if (isNaN(systemEnabled) || isNaN(isSystemOverride)) {
            console.warn(`useFeatureFlag: Invalid systemEnabled or isSystemOverride for '${featureName}', defaulting to disabled.`)
            setIsEnabled(false)
            return
        }

        if (isSystemOverride === 1) {
            // System override is on - use system setting for all tenants
            console.log(`useFeatureFlag: System override is ON for ${featureName}, using system value:`, systemEnabled === 1)
            setIsEnabled(systemEnabled === 1)
        } else {
            // System override is off - check tenant setting
            if (typeof tenantSettingForFeature === 'number') {
                const tenantEnabled = parseInt(tenantSettingForFeature, 10) === 1
                console.log(`useFeatureFlag: Using tenant setting for ${featureName}:`, tenantEnabled)
                setIsEnabled(tenantEnabled)
            } else {
                // No tenant setting - use system default
                console.log(`useFeatureFlag: No tenant setting for ${featureName}, using system value:`, systemEnabled === 1)
                setIsEnabled(systemEnabled === 1)
            }
        }
    }, [featureName, featureFlagsContext])

    // Return a boolean value to maintain compatibility with existing components
    return isEnabled
}

export default useFeatureFlag
